import type { TFunction } from "i18next";

import {
  isTranslatableItem,
  isValidDateTime,
  Log,
  McpConnectorActionStatusEnum,
  RoutePaths,
  timeDiffMinutesSigned,
  translateInfoItem,
  type IMcpConnectorActionDto,
  type IMcpConnectorActionOutcomeDto,
  type IMcpConnectorActionRequestDto,
  type SimpleResult,
} from "@ryde/shared";

import {
  MCP_CONNECTOR_ACTION_DETAILS_JSON_INDENT,
  MCP_CONNECTOR_ACTION_STATUS_MESSAGE_KEYS,
} from "./mcp-connector-action.constants";
import type {
  TConnectorActionCallResult,
  TConnectorActionPageState,
} from "./mcp-connector-action.models";

/**
 * Whether a stored login returnTo belongs to this page. The page stores its own URL before bouncing
 * through login; once the flow ends that entry must be cleared, or a later login would land on a
 * decided action. A destination owned by another flow is never ours to delete (same rule as the
 * OAuth consent page).
 */
export const isConnectorActionOwnedReturnTo = (
  returnTo: unknown,
): returnTo is string =>
  typeof returnTo === "string" &&
  returnTo.startsWith(RoutePaths.MCP_CONNECTOR_ACTION);

/** A pending action shows the decision buttons; any other status is an outcome to report. */
export const resolveConnectorActionPageState = (
  status: McpConnectorActionStatusEnum,
): TConnectorActionPageState =>
  status === McpConnectorActionStatusEnum.Pending ? "ready" : "decided";

/** The message for an action that no longer waits for a decision, or none while it still does. */
export const getConnectorActionStatusMessageKey = (
  status: McpConnectorActionStatusEnum,
): string | undefined =>
  status === McpConnectorActionStatusEnum.Pending
    ? undefined
    : MCP_CONNECTOR_ACTION_STATUS_MESSAGE_KEYS[status];

/**
 * Approve and deny act only on the action on screen, only while it waits for a decision, only when it is the
 * action the page's link names, and never twice at once. A link changed in the same tab must never let a
 * click meant for the action on screen decide another one.
 */
export const canDecideConnectorAction = ({
  action,
  actionId,
  isSubmitting,
}: {
  action: IMcpConnectorActionDto | null;
  actionId: string | null;
  isSubmitting: boolean;
}): boolean =>
  !isSubmitting &&
  action?.status === McpConnectorActionStatusEnum.Pending &&
  action.actionId === actionId;

/** Whole minutes left before the action expires, rounded up; 0 once it has expired or the date is unreadable. */
export const getMinutesLeft = ({
  expiresAt,
  nowISO,
}: {
  expiresAt: TDateISO;
  nowISO: TDateISO;
}): number => {
  if (!isValidDateTime(expiresAt) || !isValidDateTime(nowISO)) {
    return 0;
  }

  return Math.max(0, timeDiffMinutesSigned(nowISO, expiresAt));
};

/** The exact request an approval lets run, and how it went once it ran, as plain text for the details section. */
export const formatConnectorActionDetails = ({
  request,
  outcome,
}: {
  request: IMcpConnectorActionRequestDto;
  outcome: IMcpConnectorActionOutcomeDto | null;
}): string => {
  const lines = [`${request.method} ${request.path}`];
  if (request.body) {
    lines.push(
      JSON.stringify(
        request.body,
        null,
        MCP_CONNECTOR_ACTION_DETAILS_JSON_INDENT,
      ),
    );
  }
  if (outcome) {
    lines.push(`${outcome.statusCode} ${outcome.message ?? ""}`.trim());
  }

  return lines.join("\n");
};

/** A backend error as text: its translation when it carries a key, otherwise the page's generic message. */
export const translateConnectorActionError = ({
  error,
  t,
}: {
  error: unknown;
  t: TFunction;
}): string =>
  isTranslatableItem(error)
    ? translateInfoItem(error, t)
    : t("mcpConnectorAction.requestInvalid");

/**
 * Run one approval-page API call. A transport failure (axios can still throw after its retries) becomes
 * an error result, so the page never faces an unhandled rejection and stays in its state machine.
 */
export const runConnectorActionCall = async <T extends IMcpConnectorActionDto>(
  apiCall: () => Promise<SimpleResult<T> | undefined>,
): Promise<TConnectorActionCallResult<T>> => {
  try {
    const result = await apiCall();
    if (result?.success && result.data) {
      return { isSuccess: true, action: result.data };
    }

    return { isSuccess: false, error: result?.error };
  } catch (error) {
    Log.warn("AI connector action call failed", error);

    return { isSuccess: false };
  }
};
